import { useEffect, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Check, ImageOff, SunMoon, LayoutPanelLeft, Monitor, Moon, Palette, Rows3, Sun, Type, Undo2 } from 'lucide-react';
import { ACCENTS, type Density, type SidebarStyle, type ThemeMode } from '@mailpilot/shared';
import { Button, Card, CardBody, CardDivider, CardHeader, ErrorState, Segmented, Skeleton } from '../../components/ui';
import { Rows, SettingRow, SwitchRow, Well } from '../../components/settings/SettingsPrimitives';
import { useMe, useUpdateSettings } from '../../hooks/useMe';
import { SettingsTitle } from './SettingsLayout';

const MIN_SCALE = 0.875;
const MAX_SCALE = 1.375;
const STEP = 0.0625;

/** Selectable preview tile shared by the theme and sidebar pickers. */
function PickerTile({ active, onSelect, preview, label, text, icon, ariaLabel }: { active: boolean; onSelect: () => void; preview: ReactNode; label: string; text?: string; icon?: ReactNode; ariaLabel?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={ariaLabel}
      onClick={onSelect}
      className={clsx(
        'group relative flex flex-col overflow-hidden rounded-2xl bg-raised/80 text-left ring-1 transition-[box-shadow,background-color,transform] hover:bg-raised focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        active ? 'ring-2 ring-primary' : 'ring-line/70 hover:ring-line',
      )}
    >
      <div className="relative h-[5.5rem] w-full overflow-hidden">
        {preview}
        {active && (
          <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-fg shadow-primary" aria-hidden>
            <Check className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className={clsx('flex w-full items-center gap-2 border-t px-3 py-2.5', active ? 'border-primary/20 bg-primary-soft' : 'border-line/70 bg-surface')}>
        {icon && <span className={clsx('shrink-0', active ? 'text-primary-ink' : 'text-muted')}>{icon}</span>}
        <span className="min-w-0">
          <span className={clsx('block truncate text-[0.8125rem] font-medium', active ? 'text-primary-ink' : 'text-ink')}>{label}</span>
          {text && <span className={clsx('block truncate text-[0.6875rem]', active ? 'text-primary-ink/70' : 'text-muted')}>{text}</span>}
        </span>
      </div>
    </button>
  );
}

// Fixed preview palettes: illustrations of each theme, independent of the current theme.
const LIGHT = { bg: '#eef2f8', card: '#ffffff', line: '#dfe4ee', text: '#1a2233', accent: 'rgb(var(--primary))', nav: '#f7f9fc' };
const DARK = { bg: '#0d0e15', card: '#181924', line: '#303246', text: '#eef0f8', accent: 'rgb(var(--primary))', nav: '#141520' };

function ThemePreview({ p }: { p: typeof LIGHT }) {
  return (
    <div className="flex h-full w-full" style={{ background: p.bg }}>
      <div className="flex w-5 flex-col items-center gap-1.5 border-r py-2" style={{ background: p.nav, borderColor: p.line }}>
        <span className="h-2.5 w-2.5 rounded-[4px]" style={{ background: p.accent }} />
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.text, opacity: 0.25 }} />
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.text, opacity: 0.25 }} />
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.text, opacity: 0.25 }} />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-2">
        <div className="h-1.5 w-1/2 rounded-full" style={{ background: p.text, opacity: 0.7 }} />
        <div className="flex flex-1 gap-1.5">
          <div className="flex w-[42%] flex-col gap-1 rounded-md border p-1.5" style={{ background: p.card, borderColor: p.line }}>
            <div className="h-1.5 w-full rounded-full" style={{ background: p.accent, opacity: 0.9 }} />
            <div className="h-1 w-3/4 rounded-full" style={{ background: p.text, opacity: 0.2 }} />
            <div className="h-1 w-5/6 rounded-full" style={{ background: p.text, opacity: 0.2 }} />
          </div>
          <div className="flex flex-1 flex-col gap-1 rounded-md border p-1.5" style={{ background: p.card, borderColor: p.line }}>
            <div className="h-1 w-2/3 rounded-full" style={{ background: p.text, opacity: 0.45 }} />
            <div className="h-1 w-full rounded-full" style={{ background: p.text, opacity: 0.15 }} />
            <div className="h-1 w-5/6 rounded-full" style={{ background: p.text, opacity: 0.15 }} />
            <div className="mt-auto h-2 w-7 self-end rounded" style={{ background: p.accent }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemeTile({ mode, active, onSelect }: { mode: ThemeMode; active: boolean; onSelect: () => void }) {
  const label = mode === 'light' ? 'Light' : mode === 'dark' ? 'Dark' : 'System';
  const text = mode === 'light' ? 'Always light' : mode === 'dark' ? 'Always dark' : 'Follows your device';
  const Icon = mode === 'light' ? Sun : mode === 'dark' ? Moon : Monitor;
  const preview =
    mode === 'system' ? (
      <div className="flex h-full w-full">
        <div className="h-full w-1/2 overflow-hidden">
          <div className="h-full w-[200%]">
            <ThemePreview p={LIGHT} />
          </div>
        </div>
        <div className="h-full w-1/2 overflow-hidden">
          <div className="-ml-[100%] h-full w-[200%]">
            <ThemePreview p={DARK} />
          </div>
        </div>
      </div>
    ) : (
      <ThemePreview p={mode === 'light' ? LIGHT : DARK} />
    );
  return <PickerTile active={active} onSelect={onSelect} preview={preview} label={label} text={text} icon={<Icon className="h-4 w-4" aria-hidden />} />;
}

function AccentSwatch({ label, hex, active, onSelect }: { label: string; hex: string; active: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      onClick={onSelect}
      className={clsx('group flex flex-col items-center gap-2 rounded-2xl px-2 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary', active ? 'bg-primary-soft' : 'hover:bg-raised')}
    >
      <span
        className={clsx('relative flex h-9 w-9 items-center justify-center rounded-full transition-transform group-hover:scale-110', active && 'ring-2 ring-offset-2 ring-offset-surface')}
        style={{ background: `linear-gradient(145deg, ${hex} 0%, ${hex}cc 100%)`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 10px ${hex}55`, ['--tw-ring-color' as string]: hex }}
      >
        {active && <Check className="h-4 w-4 text-white drop-shadow" aria-hidden />}
      </span>
      <span className={clsx('text-xs font-medium', active ? 'text-primary-ink' : 'text-muted group-hover:text-ink')}>{label}</span>
    </button>
  );
}

const SIDEBAR_STYLES: { id: SidebarStyle; label: string; text: string }[] = [
  { id: 'light', label: 'Clean', text: 'Plain and quiet' },
  { id: 'tinted', label: 'Tinted', text: 'A wash of your accent' },
  { id: 'bold', label: 'Bold', text: 'Full accent colour' },
];

function SidebarPreview({ style: st }: { style: SidebarStyle }) {
  const nav =
    st === 'bold'
      ? { background: 'linear-gradient(180deg, rgb(var(--primary)), rgb(var(--primary-hover)))' }
      : st === 'tinted'
        ? { background: 'linear-gradient(180deg, rgb(var(--primary-soft)), rgb(var(--surface)))' }
        : { background: 'rgb(var(--surface))' };
  const line = st === 'bold' ? 'rgba(255,255,255,.7)' : 'rgb(var(--muted) / .4)';
  const activePill = st === 'bold' ? '#fff' : 'rgb(var(--primary))';
  return (
    <div className="flex h-full w-full bg-bg">
      <div className="flex w-[38%] flex-col gap-1.5 border-r border-line/70 p-2" style={nav}>
        <div className="h-2 w-2/3 rounded-full" style={{ background: line }} />
        <div className="h-2.5 w-full rounded-md" style={{ background: activePill, boxShadow: st === 'bold' ? 'none' : 'var(--shadow-primary)' }} />
        <div className="h-1.5 w-3/4 rounded-full" style={{ background: line }} />
        <div className="h-1.5 w-1/2 rounded-full" style={{ background: line }} />
      </div>
      <div className="flex-1 space-y-1.5 p-2">
        <div className="h-2 w-2/3 rounded-full bg-line" />
        <div className="h-5 rounded-md bg-surface shadow-sm" />
        <div className="h-5 rounded-md bg-surface shadow-sm" />
      </div>
    </div>
  );
}

export function AppearanceSettings() {
  const me = useMe();
  const update = useUpdateSettings();
  const settings = me.data?.settings;
  const [scale, setScale] = useState<number>(settings?.fontScale ?? 1);

  useEffect(() => {
    if (settings) setScale(settings.fontScale);
  }, [settings?.fontScale]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = <SettingsTitle title="Appearance" description="Theme, colours, layout, text size and how remote content in emails is displayed. These are your personal preferences — they sync to all your devices and don't change anyone else's view." />;

  if (me.isLoading) {
    return (
      <div>
        {title}
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-52 w-full rounded-panel" />
          <Skeleton className="h-40 w-full rounded-panel" />
          <Skeleton className="h-52 w-full rounded-panel" />
        </div>
      </div>
    );
  }
  if (me.error || !settings) {
    return (
      <div>
        {title}
        <Card>
          <ErrorState error={me.error} onRetry={() => void me.refetch()} />
        </Card>
      </div>
    );
  }

  const commitScale = (v: number) => {
    if (v !== settings.fontScale) update.mutate({ fontScale: v });
  };

  return (
    <div>
      {title}
      <div className="space-y-6">
        <Card>
          <CardHeader icon={<SunMoon className="h-5 w-5" />} title="Theme" description="System follows your device's light or dark setting automatically." />
          <CardBody>
            <div role="radiogroup" aria-label="Theme" className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
              {(['light', 'dark', 'system'] as const).map((m) => (
                <ThemeTile key={m} mode={m} active={settings.theme === m} onSelect={() => update.mutate({ theme: m })} />
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader icon={<Palette className="h-5 w-5" />} title="Accent colour" description="Used for buttons, the selected conversation, highlights and the notification island." />
          <CardBody className="!pt-4">
            <div role="radiogroup" aria-label="Accent colour" className="grid grid-cols-4 gap-1 sm:grid-cols-8">
              {ACCENTS.map((a) => (
                <AccentSwatch key={a.id} label={a.label} hex={a.hex} active={settings.accent === a.id} onSelect={() => update.mutate({ accent: a.id })} />
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader icon={<LayoutPanelLeft className="h-5 w-5" />} title="Navigation" description="How much colour the dock and navigation carry." />
          <CardBody>
            <div role="radiogroup" aria-label="Sidebar style" className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
              {SIDEBAR_STYLES.map((st) => (
                <PickerTile key={st.id} active={settings.sidebarStyle === st.id} onSelect={() => update.mutate({ sidebarStyle: st.id })} preview={<SidebarPreview style={st.id} />} label={st.label} text={st.text} />
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader icon={<Rows3 className="h-5 w-5" />} title="Reading & layout" description="Density of the message list and the size of text throughout MailPilot." />
          <CardBody>
            <Rows>
              <SettingRow
                label="Message list density"
                description="Comfortable shows a two-line preview; compact fits more conversations on screen."
                control={
                  <Segmented<Density>
                    label="Message list density"
                    value={settings.density}
                    onChange={(v) => update.mutate({ density: v })}
                    options={[
                      { value: 'comfortable', label: 'Comfortable' },
                      { value: 'compact', label: 'Compact' },
                    ]}
                  />
                }
              />
              <div className="py-4 last:pb-0">
                <div className="flex items-start justify-between gap-6">
                  <div className="min-w-0">
                    <label htmlFor="font-scale" className="font-medium text-ink">
                      Text size
                    </label>
                    <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted">Scales text everywhere, including the reading pane.</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {scale !== 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setScale(1);
                          commitScale(1);
                        }}
                      >
                        Reset
                      </Button>
                    )}
                    <span className="min-w-[3.25rem] rounded-lg bg-sunken px-2 py-1 text-center text-[0.8125rem] font-semibold tabular-nums text-ink">{Math.round(scale * 100)}%</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <Type className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
                  <input
                    id="font-scale"
                    type="range"
                    min={MIN_SCALE}
                    max={MAX_SCALE}
                    step={STEP}
                    value={scale}
                    aria-valuetext={`${Math.round(scale * 100)} percent`}
                    onChange={(e) => setScale(Number(e.target.value))}
                    onPointerUp={(e) => commitScale(Number(e.currentTarget.value))}
                    onKeyUp={(e) => commitScale(Number(e.currentTarget.value))}
                    onBlur={(e) => commitScale(Number(e.currentTarget.value))}
                    className="h-2 flex-1 cursor-pointer accent-[rgb(var(--primary))]"
                  />
                  <Type className="h-5 w-5 shrink-0 text-muted" aria-hidden />
                </div>
                <Well className="mt-4">
                  <div style={{ fontSize: `${scale}rem` }} aria-live="polite">
                    <p className="font-semibold text-ink" style={{ fontSize: '1em' }}>
                      Re: Quarterly report
                    </p>
                    <p className="mt-1 leading-relaxed text-muted" style={{ fontSize: '0.875em' }}>
                      Thanks for sending this over. I've reviewed the figures and everything looks good — let's go ahead with Thursday's meeting.
                    </p>
                  </div>
                </Well>
              </div>
            </Rows>
          </CardBody>
        </Card>

        <Card>
          <CardHeader icon={<Undo2 className="h-5 w-5" />} title="Undo send" description="After you press Send, the message waits a moment so you can take it back. Applies on all your devices." />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <Rows>
              <SettingRow
                label="Time to undo"
                description="Off sends immediately."
                control={<Segmented<string> label="Time to undo" value={String(settings.undoSendSeconds ?? 10)} onChange={(v) => update.mutate({ undoSendSeconds: Number(v) })} options={[0, 5, 10, 20, 30].map((s) => ({ value: String(s), label: s ? `${s} s` : 'Off' }))} />}
              />
            </Rows>
          </CardBody>
        </Card>

        <Card>
          <CardHeader icon={<ImageOff className="h-5 w-5" />} title="Remote content" description="Protect your privacy when opening emails." />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <Rows>
              <SwitchRow
                id="block-remote-images"
                checked={settings.blockRemoteImages}
                onChange={(v) => update.mutate({ blockRemoteImages: v })}
                label="Block remote images by default"
                description="Many senders embed invisible tracking pixels that reveal when, where and on which device you opened their email. With this on, images loaded from the internet stay hidden until you choose “Show images” on a message. Attached and embedded images still display."
              />
            </Rows>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
